// =============================================================================
// MentionTextarea — Textarea mit @-Autocomplete für Teammitglieder
// MentionText     — zeigt gespeicherten Text mit hervorgehobenen Erwähnungen
//
// Die Textarea speichert Erwähnungen als Token `@[Name](uuid)` (siehe
// lib/mentions.js). Das Dropdown öffnet sich, sobald man „@" tippt, und filtert
// die Profile nach dem getippten Namen. Auswahl fügt das Token ein.
// =============================================================================
import { useState, useRef, useLayoutEffect, useMemo } from 'react';
import { Avatar } from './components.jsx';
import { activeMentionQuery, mentionToken, splitMentions } from './lib/mentions.js';

const MAX_SUGGESTIONS = 6;

export function MentionTextarea({
  value, onChange, profiles = [],
  className = 'textarea', placeholder, rows, style, autoFocus, id, disabled,
}) {
  const ref = useRef(null);
  const [menu, setMenu] = useState(null); // { query, start, index } | null
  // Nach dem Einfügen eines Tokens den Cursor gezielt setzen (nach Re-Render).
  const pendingCaret = useRef(null);

  useLayoutEffect(() => {
    if (pendingCaret.current != null && ref.current) {
      ref.current.selectionStart = ref.current.selectionEnd = pendingCaret.current;
      pendingCaret.current = null;
    }
  });

  const suggestions = useMemo(() => {
    if (!menu) return [];
    const q = menu.query.toLowerCase();
    return profiles
      .filter(p => {
        const name = (p.full_name || '').toLowerCase();
        const email = (p.email || '').toLowerCase();
        return q === '' || name.includes(q) || email.includes(q);
      })
      .slice(0, MAX_SUGGESTIONS);
  }, [menu, profiles]);

  // Menü anhand der aktuellen Cursorposition aktualisieren.
  function syncMenu(text, caret) {
    const hit = activeMentionQuery(text, caret);
    setMenu(hit ? { query: hit.query, start: hit.start, index: 0 } : null);
  }

  function handleChange(e) {
    onChange(e.target.value);
    syncMenu(e.target.value, e.target.selectionStart);
  }

  function pick(profile) {
    if (!profile || !menu) return;
    const el = ref.current;
    const caret = el ? el.selectionStart : value.length;
    const before = value.slice(0, menu.start);
    const after = value.slice(caret);
    const token = mentionToken(profile.full_name || profile.email, profile.id);
    const next = `${before}${token} ${after}`;
    pendingCaret.current = (before + token + ' ').length;
    onChange(next);
    setMenu(null);
  }

  function handleKeyDown(e) {
    if (!menu || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setMenu(m => ({ ...m, index: (m.index + 1) % suggestions.length }));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setMenu(m => ({ ...m, index: (m.index - 1 + suggestions.length) % suggestions.length }));
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      pick(suggestions[menu.index]);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setMenu(null);
    }
  }

  return (
    <div style={{ position: 'relative' }}>
      <textarea
        ref={ref}
        id={id}
        className={className}
        placeholder={placeholder}
        rows={rows}
        style={style}
        autoFocus={autoFocus}
        disabled={disabled}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onKeyUp={e => syncMenu(e.target.value, e.target.selectionStart)}
        onClick={e => syncMenu(e.target.value, e.target.selectionStart)}
        onBlur={() => setTimeout(() => setMenu(null), 120)}
      />
      {menu && suggestions.length > 0 && (
        <div className="mention-menu">
          {suggestions.map((p, i) => (
            <button
              type="button"
              key={p.id}
              className={i === menu.index ? 'mention-option active' : 'mention-option'}
              // onMouseDown statt onClick: feuert vor dem Textarea-blur.
              onMouseDown={e => { e.preventDefault(); pick(p); }}
              onMouseEnter={() => setMenu(m => (m ? { ...m, index: i } : m))}
            >
              <Avatar user={p} size={22}/>
              <span className="mention-option-name">{p.full_name || p.email}</span>
              {p.full_name && <span className="mention-option-mail">{p.email}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Zeigt gespeicherten Text mit hervorgehobenen @Erwähnungen (read-only).
export function MentionText({ text, style }) {
  const segments = splitMentions(text || '');
  if (segments.length === 0) return null;
  return (
    <span style={style}>
      {segments.map((s, i) =>
        s.type === 'mention'
          ? <span key={i} className="mention-chip">{s.value}</span>
          : <span key={i}>{s.value}</span>
      )}
    </span>
  );
}
